import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';

import { Observable } from 'rxjs';

import { adaptPlainObjectToCamelCase } from '@lib/commons/util-functions/plain-object-to-pascal.function';

import { DoctorWithScheduleModel, ServiceScheduleSlotsModel } from '@lib/core/models/doctor.interface';
import { LimitRule } from '@lib/core/models/insurance-package.interface';
import { LaboratoriesRequestModel, LaboratoriesResponseModel } from '@lib/core/models/laboratory.interface';
import { PagedResponse } from '@lib/core/models/paged-response';
import { PharmacyBasketModel } from '@lib/core/models/pharmacy.interface';
import { LiloDrugSearchResponse, MedicationBasketRequest } from '@lib/core/models/protocol.interface';
import { API_URL, PROTECTED_API_URL } from '@lib/core/tokens/api.tokens';

import { GuaranteeApplication } from '../models/guarantee-application.interface';
import { PHARMACIES_LATITUDE, PHARMACIES_LONGITUDE, PHARMACIES_MAX_SEARCH_DISTANCE_METERS } from '../models/pharmacies.const';
import {
  GenericServiceValue,
  MedicalServiceRequestBody,
  PersonalDoctorMetadata,
  PersonalDoctorValue,
  PharmacologicalItemMetaData,
  PharmacologicalTreatmentValue,
  Prescription,
  PrescriptionCreateRequest,
  PrescriptionFormValue,
  PrescriptionItem,
  PrescriptionUpdateRequest,
  PrescriptionsSearch,
  RecordDeleteRequest,
  ServiceMetadata,
} from '../models/prescription.interface';

export interface PrescriptionPlanningRequestModel<T> {
  timeSlotId?: string;
  prescriptionIds: string[];
  partnerId: string;
  partnerLocationId: string;
  executeIntegration: boolean;
  doctorId?: string;
  doctorName?: string;
  startDate?: string;
  endDate?: string;
  price?: number;
  details: T;
}

@Injectable({
  providedIn: 'root',
})
export class PrescriptionsService {
  private readonly http = inject(HttpClient);
  private readonly apiUrl = inject(API_URL);
  private readonly protectedApiUrl = inject(PROTECTED_API_URL);

  createPrescription(memberId: string, applicationId: string, prescription: unknown): Observable<Prescription> {
    return this.http.post<Prescription>(`${this.apiUrl}/members/${memberId}/guarantee-applications/${applicationId}/prescriptions`, prescription);
  }

  updatePrescription(memberId: string, applicationId: string, prescriptionId: string, prescription: unknown): Observable<Prescription> {
    return this.http.put<Prescription>(`${this.apiUrl}/members/${memberId}/guarantee-applications/${applicationId}/prescriptions/${prescriptionId}`, prescription);
  }

  deletePrescription(prescriptionId: string, request: RecordDeleteRequest = {}): Observable<void> {
    return this.http.delete<void>(`${this.protectedApiUrl}/prescriptions/${prescriptionId}`, { body: request });
  }

  processPrescriptionDraftsCreate(memberId: string, drafts: unknown[]): Observable<unknown> {
    return this.http.post<unknown>(`${this.apiUrl}/members/${memberId}/prescriptions/bulk/create`, drafts);
  }

  processPrescriptionsDraftsUpdate(memberId: string, drafts: unknown[]): Observable<unknown> {
    return this.http.put<unknown>(`${this.apiUrl}/members/${memberId}/prescriptions/bulk/update`, drafts);
  }

  planPrescription<T>(data: PrescriptionPlanningRequestModel<T>) {
    return this.http.post(`${this.apiUrl}/members/prescriptions/create`, data);
  }

  searchLaboratories(request: LaboratoriesRequestModel) {
    return this.http.post<LaboratoriesResponseModel>(`${this.apiUrl}/laboratories/search`, request);
  }

  getMedicationBasket(requestBody: MedicationBasketRequest): Observable<PharmacyBasketModel> {
    return this.http.post<PharmacyBasketModel>(`${this.apiUrl}/pharmacies`, requestBody);
  }

  createNewGuaranteeBasket(prescriptions: PrescriptionItem<PharmacologicalItemMetaData>[]) {
    const combinedProducts = [];
    prescriptions.forEach(prescription => {
      const medicationMetadata = prescription.metadata as PharmacologicalItemMetaData;
      const morion_items: LiloDrugSearchResponse[] = medicationMetadata.trademarks ? JSON.parse(medicationMetadata.trademarks) : [];
      const prescriptionId = crypto.randomUUID();
      const products = morion_items.map(item => ({
        prescriptionId,
        code: item.code,
        identifier: item.identifier,
        substanceQuantity: item.substanceQuantity,
        quantityInPackage: item.quantityInPackage ?? 0,
        form: item.formUa,
        name: item.nameFullUa,
        requiredQuantity: +medicationMetadata.treatment_dose,
      }));
      combinedProducts.push(...products);
    });
    const requestBody: MedicationBasketRequest = {
      uniqueId: crypto.randomUUID(),
      products: combinedProducts,
      latitude: PHARMACIES_LATITUDE,
      longitude: PHARMACIES_LONGITUDE,
      maxDistanceInMeters: PHARMACIES_MAX_SEARCH_DISTANCE_METERS,
      requireNewFormation: true,
    };
    return requestBody;
  }

  createMedicationProcessBody(formValue: PrescriptionFormValue<PharmacologicalTreatmentValue>) {
    const updatedDateTime = new Date(formValue.endDate);
    updatedDateTime.setHours(23, 59, 59, 999);
    return <PrescriptionItem<PharmacologicalItemMetaData>>{
      type: formValue.type,
      name: formValue.name,
      startDate: formValue.startDate,
      endDate: updatedDateTime,
      guaranteeStatus: formValue.guaranteeStatus,
      reason: formValue.reason,
      metadata: {
        single_dose: formValue.metadata.singleDose.toString(),
        form: formValue.metadata.form.toString(),
        frequency: formValue.metadata.frequency,
        treatment_dose: formValue.metadata.treatmentDose.toString(),
        daily_amount: formValue.metadata.dailyAmount.toString(),
        duration: formValue.metadata.duration.toString(),
        inn: formValue.metadata.inn,
        inn_ua: formValue.metadata.morionSubstance,
        trademarks: JSON.stringify(adaptPlainObjectToCamelCase(formValue.metadata.morionItems)),
      },
      sendGuaranteeNotification: formValue.sendGuaranteeNotification,
      franchise: formValue.franchise,
      completedInAppointment: formValue.completedInAppointment,
      completionReason: formValue.completedInAppointment ? formValue.completionReason : null,
      id: formValue.id,
      guaranteeId: formValue.guaranteeId,
      originalPrescriptionId: formValue.originalPrescriptionId,
      categories: formValue.categories ?? [],
    };
  }

  createPersonalDoctorProcessBody(formValue: PrescriptionFormValue<PersonalDoctorValue>) {
    const updatedDateTime = new Date(formValue.endDate);
    updatedDateTime.setHours(23, 59, 59, 999);
    return <PrescriptionItem<PersonalDoctorMetadata>>{
      type: formValue.type,
      name: formValue.name,
      startDate: formValue.startDate,
      endDate: updatedDateTime,
      guaranteeStatus: formValue.guaranteeStatus,
      reason: formValue.reason,
      metadata: {
        service_id: formValue.metadata.serviceId,
        doctor_id: formValue.metadata.doctorId,
        doctor_partner_id: formValue.metadata.doctorPartnerId ?? null,
      },
      sendGuaranteeNotification: formValue.sendGuaranteeNotification,
      franchise: formValue.franchise,
      completedInAppointment: formValue.completedInAppointment,
      completionReason: formValue.completedInAppointment ? formValue.completionReason : null,
      id: formValue.id,
      guaranteeId: formValue.guaranteeId,
      originalPrescriptionId: formValue.originalPrescriptionId,
      categories: formValue.categories ?? [],
    };
  }

  createServiceProcessBody(formValue: PrescriptionFormValue<GenericServiceValue>) {
    const updatedDateTime = new Date(formValue.endDate);
    updatedDateTime.setHours(23, 59, 59, 999);
    return <PrescriptionItem<ServiceMetadata>>{
      type: formValue.type,
      name: formValue.name,
      startDate: formValue.startDate,
      endDate: updatedDateTime,
      guaranteeStatus: formValue.guaranteeStatus,
      reason: formValue.reason,
      metadata: {
        code: formValue.metadata.code,
        quantity: formValue.metadata.quantity.toString(),
      },
      sendGuaranteeNotification: formValue.sendGuaranteeNotification,
      franchise: formValue.franchise,
      completedInAppointment: formValue.completedInAppointment,
      completionReason: formValue.completedInAppointment ? formValue.completionReason : null,
      id: formValue.id,
      guaranteeId: formValue.guaranteeId,
      originalPrescriptionId: formValue.originalPrescriptionId,
      categories: formValue.categories ?? [],
    };
  }

  getPrescriptionLimits(memberId: string, guaranteeId: string, prescriptionId: string): Observable<LimitRule[]> {
    return this.http.get<LimitRule[]>(`${this.apiUrl}/members/${memberId}/guarantee-applications/${guaranteeId}/prescriptions/${prescriptionId}/recalculate`);
  }

  getDobrobutDoctorSlots(prescriptionIds: string[], doctorId?: string, locationId?: string) {
    return this.http.post<DoctorWithScheduleModel[]>(`${this.apiUrl}/members/prescriptions/slots`, { doctorId, prescriptionIds, locationId });
  }

  getPersonalDoctorSlots(prescriptionIds: string[], doctorId?: string, locationId?: string) {
    return this.http.post<ServiceScheduleSlotsModel[]>(`${this.apiUrl}/members/prescriptions/timeslots`, { doctorId, prescriptionIds, locationId });
  }

  POCPrescriptionsSearch(search: PrescriptionsSearch) {
    return this.http.post<PagedResponse<Prescription>>(`${this.apiUrl}/prescriptions/search`, search);
  }

  getPrescriptionById(prescriptionId: string): Observable<Prescription> {
    return this.http.get<Prescription>(`${this.apiUrl}/prescriptions/${prescriptionId}`);
  }

  postMedicalService(memberId: string, body: MedicalServiceRequestBody): Observable<Partial<GuaranteeApplication>> {
    return this.http.post<Partial<GuaranteeApplication>>(`${this.apiUrl}/members/${memberId}/guarantee-applications`, body);
  }

  createManagedPrescription(memberId: string, prescription: PrescriptionCreateRequest): Observable<Prescription> {
    return this.http.post<Prescription>(`${this.protectedApiUrl}/prescriptions`, { memberId, prescription });
  }

  updateManagedPrescription(memberId: string, prescription: PrescriptionUpdateRequest): Observable<Prescription> {
    return this.http.put<Prescription>(`${this.protectedApiUrl}/prescriptions/${prescription.id}`, { memberId, prescription });
  }

  updateManagedPlanning(
    prescriptionId: string,
    planningId: string,
    request: { price?: number; franchise?: number; planningStatus?: string; dateStart?: string; dateEnd?: string }
  ): Observable<void> {
    return this.http.put<void>(`${this.protectedApiUrl}/prescriptions/${prescriptionId}/plannings/${planningId}`, request);
  }
}
